import { Plus } from "lucide-react";
import { formatDate } from "@/lib/format";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  jobsApi,
  type JobListItemDto,
  type ListJobsParams,
} from "@/lib/jobs";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/app/auth-context";
import { NewJobWizard } from "./NewJobWizard";

// ---------------------------------------------------------------------------
// Status-badge variant + initials helpers
// ---------------------------------------------------------------------------

// Maps the wire-format job status code (see JobListItemDto.currentStatusCode)
// to a Badge variant. The legacy enum uses PascalCase codes like "InProgress",
// not the SCREAMING_CASE shown in the plan — we map them here.
function statusColorForJob(status: string): string {
  switch (status) {
    case "OnHold":
      return "bg-warning/15 text-warning-foreground";
    case "Pending":
    case "InProgress":
      return "bg-info/15 text-primary";
    case "Completed":
    case "Invoiced":
      return "bg-success/15 text-success";
    case "Cancelled":
      return "bg-destructive/15 text-destructive";
    default:
      return "bg-primary text-primary-foreground";
  }
}

function formatJobStatus(status: string): string {
  switch (status) {
    case "InProgress":         return "In Progress";
    case "ReadyForProduction": return "Ready for Prod";
    case "InProduction":       return "In Production";
    case "OnHold":             return "On Hold";
    default:                   return status;
  }
}

// ---------------------------------------------------------------------------
// Filter state — single search + chip-based status grouping
// ---------------------------------------------------------------------------

type StatusChipId = "active" | "completed" | "archived" | "all";

interface FilterState {
  q: string;
  chip: StatusChipId;
}

const EMPTY_FILTERS: FilterState = { q: "", chip: "active" };

const STATUS_CHIPS = [
  { id: "active" as const,    label: "Show Active" },
  { id: "completed" as const, label: "Completed" },
  { id: "archived" as const,  label: "Archived" },
  { id: "all" as const,       label: "All" },
];

export const STATUS_GROUPS: Record<StatusChipId, string[] | undefined> = {
  active:    ["Pending", "InProgress", "ReadyForProduction", "InProduction", "OnHold"],
  completed: ["Completed", "Dispatched", "Invoiced"],
  archived:  ["Cancelled"],
  all:       undefined,
};

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// Build API params from filter state
// ---------------------------------------------------------------------------

function buildParams(
  state: FilterState,
  pageIndex: number,
  pageSize: number,
  clientId: number | undefined,
): ListJobsParams {
  return {
    q: state.q || undefined,
    status: STATUS_GROUPS[state.chip],
    clientId,
    page: pageIndex + 1,
    pageSize,
  };
}

// ---------------------------------------------------------------------------
// JobsListPage
// ---------------------------------------------------------------------------

export function JobsListPage() {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [newJobModalOpen, setNewJobModalOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdParam = searchParams.get("clientId");
  // Coerce defensively: NaN, 0, and negatives all collapse to undefined so
  // the chip never reads "Client #NaN" and we never send a bogus filter.
  const parsedClientId = clientIdParam ? Number(clientIdParam) : NaN;
  const clientIdFilter =
    Number.isFinite(parsedClientId) && parsedClientId > 0 ? parsedClientId : undefined;
  const clearClientFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("clientId");
    setSearchParams(next);
    setPageIndex(0);
  };

  const params = useMemo(
    () => buildParams(filters, pageIndex, pageSize, clientIdFilter),
    [filters, pageIndex, pageSize, clientIdFilter],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["jobs", params],
    queryFn: () => jobsApi.list(params),
  });

  // Sort state — client-side only; backend (jobsApi.list) has no `sort` param.
  // Sort applies to the current page's rows, not the full result set.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const visibleRows = useMemo(() => {
    const rows = data?.items ?? [];
    if (!sort) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: JobListItemDto): string | number | null => {
      switch (sort.columnId) {
        case "status-pill": return r.currentStatusCode;
        case "ref":         return r.jobReferenceNo;
        case "client":      return r.clientName ?? null;
        case "owner":       return r.ownerEmployeeName ?? null;
        case "dispatch":    return r.expectedDispatchDate ?? null;
        case "total":       return r.totalIncGst ?? null;
        default:            return null;
      }
    };
    return [...rows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data, sort]);

  const cols: DataGridColumn<JobListItemDto>[] = [
    {
      id: "ref",
      header: "Job #",
      sortable: true,
      accessor: (r) => (
        <Link to={`/jobs/${r.jobId}`} className="underline text-primary">
          {r.jobReferenceNo}
        </Link>
      ),
    },
    {
      id: "client",
      header: "Client",
      sortable: true,
      accessor: (r) => r.clientName ?? "—",
    },
    {
      id: "owner",
      header: "Owner",
      sortable: true,
      accessor: (r) => r.ownerEmployeeName ?? "—",
    },
    {
      id: "status-pill",
      header: "Status",
      width: "120px",
      sortable: true,
      accessor: (r) => (
        <span
          className={cn(
            "inline-block rounded px-2 py-0.5 text-xs font-medium",
            statusColorForJob(r.currentStatusCode)
          )}
        >
          {formatJobStatus(r.currentStatusCode)}
        </span>
      ),
    },
    {
      id: "dispatch",
      header: "Expected dispatch",
      sortable: true,
      accessor: (r) =>
        r.expectedDispatchDate
          ? formatDate(r.expectedDispatchDate)
          : "—",
    },
    {
      id: "total",
      header: "Total (inc. GST)",
      sortable: true,
      accessor: (r) => (r.totalIncGst != null ? fmt.format(r.totalIncGst) : "—"),
      align: "right",
    },

  ];

  return (
    <ListPage
      title="Jobs"
      primaryAction={
        permissions.has("jobs:write") ? (
          <Button onClick={() => setNewJobModalOpen(true)} aria-label="New job">
            <Plus className="mr-1.5 h-4 w-4" /> Add Job
          </Button>
        ) : undefined
      }
      search={{
        value: filters.q,
        onChange: (v) => { setFilters((f) => ({ ...f, q: v })); setPageIndex(0); },
        placeholder: "Search ref, customer, description, PO #…",
      }}
      filterChips={{
        options: STATUS_CHIPS,
        value: filters.chip,
        onChange: (next) => { setFilters((f) => ({ ...f, chip: next })); setPageIndex(0); },
        "aria-label": "Show jobs by status",
      }}
      filterBar={
        clientIdFilter != null ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1 text-xs">
              Filtered by Client #{clientIdFilter}
              <button
                type="button"
                onClick={clearClientFilter}
                aria-label="Clear client filter"
                className="text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </span>
          </div>
        ) : undefined
      }
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.jobId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={pageIndex}
        pageSize={pageSize}
        loading={isLoading}
        emptyHeadline="No jobs match these filters."
        onPageChange={setPageIndex}
        onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
        onSortChange={setSort}
        onRowClick={(r) => navigate(`/jobs/${r.jobId}`)}
      />
      {permissions.has("jobs:write") && (
        <NewJobWizard open={newJobModalOpen} onOpenChange={setNewJobModalOpen} />
      )}
    </ListPage>
  );
}
